import { SELECTABLE_PILL, SELECTABLE_PILL_OFF, SELECTABLE_PILL_ON } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface WorkbenchPromptModeRailProps {
  modes: readonly { id: string; label: string; description: string; disabled?: boolean }[];
  selectedId: string | null;
  /** Called with the mode id, or null when the active chip is clicked again. */
  onSelect: (id: string | null) => void;
  className?: string;
}

/**
 * The edit-mode chip rail, on the prompt bar's quick-actions row.
 *
 * WHY IT IS NOT A `+` MENU ANY MORE. The five modes used to live in a dropdown
 * behind a `+`, and picking one dispatched the request IMMEDIATELY — except
 * `useWorkbenchPrompt.submit` returns on an empty prompt, so picking a mode with
 * nothing typed did nothing at all, silently. So the menu was simultaneously
 * hiding the feature and, when found, not performing it.
 *
 * A chip rail fixes both halves at once: the modes are visible without a click,
 * and a chip SELECTS a mode rather than firing one — it prefills the composer
 * with that mode's instruction, which the user then edits and sends like any
 * other prompt. The mode rides along on the request; nothing classifies text.
 *
 * These are genuine toggles, which is what `SELECTABLE_PILL` is scoped to
 * (`ui-tokens.ts`) — unlike the header's old "Audience set" spans, which wore
 * the same token while being static metadata.
 *
 * This component is deliberately mode-agnostic: it takes labelled options, not
 * `PROMPT_MODES`, so the generic workbench library keeps no dependency on the
 * concept-workbench layer that owns the vocabulary.
 */
export function WorkbenchPromptModeRail({
  modes,
  selectedId,
  onSelect,
  className,
}: WorkbenchPromptModeRailProps): JSX.Element {
  return (
    <div
      role="group"
      aria-label="Edit modes"
      className={cn('flex shrink-0 items-center gap-1.5', className)}
    >
      {modes.map((mode) => {
        const isSelected = mode.id === selectedId;
        return (
          <button
            key={mode.id}
            type="button"
            aria-pressed={isSelected}
            disabled={mode.disabled}
            title={mode.description}
            onClick={() => onSelect(isSelected ? null : mode.id)}
            className={cn(
              SELECTABLE_PILL,
              isSelected ? SELECTABLE_PILL_ON : SELECTABLE_PILL_OFF,
              'h-6 shrink-0 whitespace-nowrap px-2.5 py-0 text-detail',
              'disabled:pointer-events-none disabled:opacity-40',
            )}
          >
            {mode.label}
          </button>
        );
      })}
    </div>
  );
}
